// Keeps the live telescope position from redrawing the Plotly charts more often than needed.
import { useCallback, useEffect, useRef, useState } from 'react'

// At most one change per interval; the latest value is applied when the interval ends.
export function useThrottled<T>(value: T, intervalMs: number): T {
  const [shown, setShown] = useState(value)
  const lastChange = useRef(0)

  useEffect(() => {
    if (Object.is(value, shown)) return undefined
    const apply = () => {
      lastChange.current = Date.now()
      setShown(value)
    }
    const wait = lastChange.current + intervalMs - Date.now()
    if (wait <= 0) {
      apply()
      return undefined
    }
    const timer = setTimeout(apply, wait)
    return () => clearTimeout(timer)
  }, [value, shown, intervalMs])

  return shown
}

// Plotly saves a dragged 3D camera only on mouseup over the canvas; a redraw before that snaps the
// view back to where the drag started. The value is held from the first `plotly_relayouting` until
// the mouse is released. The window listener runs after Plotly's own one on the canvas.
export function useHeldWhileDragging<T>(value: T) {
  const [dragging, setDragging] = useState(false)
  const held = useRef(value)
  if (!dragging) held.current = value

  useEffect(() => {
    if (!dragging) return undefined
    const release = () => setDragging(false)
    window.addEventListener('mouseup', release)
    window.addEventListener('touchend', release)
    return () => {
      window.removeEventListener('mouseup', release)
      window.removeEventListener('touchend', release)
    }
  }, [dragging])

  const onRelayouting = useCallback(() => setDragging(true), [])

  return { value: held.current, onRelayouting }
}

// Follows the value one painted frame later. A control bound to the value itself shows the new
// choice at once; the heavy work driven by the returned value starts only after that frame.
export function useAfterPaint<T>(value: T): T {
  const [shown, setShown] = useState(value)

  useEffect(() => {
    if (Object.is(shown, value)) return undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    // requestAnimationFrame runs just before the next paint, the timeout just after it.
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => setShown(value))
    })
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [value, shown])

  return shown
}
